import React, { Component } from 'react';
import { Box, Typography } from '@mui/material';
import SectionTitle from './SectionTitle';
import CustomLightButton from '../../../../../components/CustomLightButton';
import RadioButtonGroup from '../../../../../components/RadioButtonGroup';
import DeletableChip from '../../../../../components/DeletableChip';
import PaginationWrapper from '../../../../../components/PaginationWrapper';
import DeletableNumericInput from '../../../../../components/DeletableNumericInput';
import { ALERT_SEVERITY } from '../../../../../components/AlertDisplay2';

export const PRODUCT_RESTRICTION_SELECTION_TYPES = {
    NO_RESTRICTIONS: 'NO_RESTRICTION_PRODUCT',
    ONLY_SELECTED: 'ONLY_SELECTED_PRODUCTS',
};

export const PRODUCT_SCOPE_TYPE = {
    VALID_FOR: 'VALID_FOR',
    NOT_VALID_FOR: 'NOT_VALID_FOR',
};

class ProductConfigSection extends Component {
    constructor(props) {
        super(props);

        const { selectedProducts = [] } = props;

        this.productSelectionTypes = [
            { key: PRODUCT_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS, label: 'Keine Einschränkung' },
            { key: PRODUCT_RESTRICTION_SELECTION_TYPES.ONLY_SELECTED, label: 'Nur ausgewählte Produkte' },
        ];

        this.productScopeTypes = [
            { key: PRODUCT_SCOPE_TYPE.VALID_FOR, label: 'Gültig für' },
            { key: PRODUCT_SCOPE_TYPE.NOT_VALID_FOR, label: 'Nicht gültig für' },
        ];

        this.state = {
            mustHaveMinQty: selectedProducts.some((p) => p.minQty != null) || false,
        };
    }

    normalize = (s) => (s ?? '').toString().toLowerCase().trim();

    deleteById = (id) => {
        const {
            selectedProducts,
            setSelectedProducts,
            editMode,
            setSelectedOptions,
            selectedOptions,
        } = this.props;

        const myProducts = Array.isArray(selectedProducts) ? [...selectedProducts] : [];
        const index = myProducts.findIndex((p) => this.normalize(p?.id) === this.normalize(id));

        if (index >= 0 && index < myProducts.length) {
            if (editMode) {
                myProducts[index] = { ...myProducts[index], deleted: true };
            } else {
                myProducts.splice(index, 1);
            }

            const deletedCount = myProducts.filter((p) => p.deleted).length;

            if (deletedCount === myProducts.length) {
                myProducts.forEach((p) => delete p.deleted);
                setSelectedProducts?.(myProducts);
                setSelectedOptions?.({
                    ...selectedOptions,
                    [editMode ? 'newSelectionType' : 'selectionType']:
                        PRODUCT_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS,
                });
            } else {
                setSelectedProducts?.(myProducts);
            }
        }
    };

    reselectById(id) {
        const { selectedProducts, setSelectedProducts, editMode } = this.props;
        const myProducts = Array.isArray(selectedProducts) ? [...selectedProducts] : [];
        const index = myProducts.findIndex((p) => this.normalize(p?.id) === this.normalize(id));

        if (index >= 0 && index < myProducts.length) {
            if (editMode) {
                const { deleted, ...rest } = myProducts[index];
                myProducts[index] = rest;
                setSelectedProducts?.(myProducts);
            }
        }
    }

    componentDidUpdate(prevProps) {
        const { selectedProducts = [] } = this.props;

        if (prevProps.selectedProducts !== selectedProducts) {
            const mustHaveMinQty = selectedProducts.some((p) => p.minQty != null);
            if (mustHaveMinQty !== this.state.mustHaveMinQty) {
                this.setState({ mustHaveMinQty });
            }
        }
    }

    render() {
        const {
            selectedOptions,
            setSelectedOptions,
            selectedProducts = [],
            setSelectedProducts,
            editMode,
            onSelectionPressed,
            showAlert,
        } = this.props;

        const mySelectionType =
            selectedOptions?.newSelectionType ?? selectedOptions?.selectionType;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <SectionTitle
                    icon={require('../../../../../assets/icons/dairyproducts.png')}
                    label="Produkte"
                />

                <RadioButtonGroup
                    items={this.productSelectionTypes}
                    selectedKey={mySelectionType}
                    onSelect={(item) => {
                        if (editMode) {
                            if (item === PRODUCT_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS) {
                                setSelectedOptions?.({ ...selectedOptions, newSelectionType: item });
                            } else if (
                                item === PRODUCT_RESTRICTION_SELECTION_TYPES.ONLY_SELECTED &&
                                selectedOptions?.newSelectionType ===
                                PRODUCT_RESTRICTION_SELECTION_TYPES.NO_RESTRICTIONS
                            ) {
                                const myOptions = { ...selectedOptions };
                                delete myOptions.newSelectionType;
                                setSelectedOptions?.({ ...myOptions });
                            } else {
                                showAlert?.('Die Daten können nicht bearbeitet werden.', ALERT_SEVERITY.WARNING);
                            }
                        } else {
                            setSelectedOptions?.({ ...selectedOptions, selectionType: item });
                        }
                    }}
                />

                {mySelectionType === PRODUCT_RESTRICTION_SELECTION_TYPES.ONLY_SELECTED && (
                    <>
                        <RadioButtonGroup
                            items={this.productScopeTypes}
                            selectedKey={selectedOptions?.scopeType}
                            onSelect={(item) => {
                                if (!editMode) {
                                    setSelectedOptions?.({ ...selectedOptions, scopeType: item });
                                } else {
                                    showAlert?.('Die Daten können nicht bearbeitet werden.', ALERT_SEVERITY.WARNING);
                                }
                            }}
                        />

                        {(selectedProducts?.length ?? 0) > 0 && (
                            <PaginationWrapper
                                items={selectedProducts ?? []}
                                pageSize={5}
                                threshold={5}
                                disablePaginationOnSearch={true}
                                showSearch={(selectedProducts?.length ?? 0) > 5}
                                filterFn={(prod, term) => {
                                    const name = this.normalize(prod?.name);
                                    const id = this.normalize(prod?.id);
                                    const shop = this.normalize(prod?.shopInfo?.name);
                                    return (
                                        name.includes(term) ||
                                        id.includes(term) ||
                                        shop.includes(term)
                                    );
                                }}
                            >
                                {(pagedItems) => (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {pagedItems.map((prod) => {
                                            const textColor = prod.deleted ? '#979797' : '#424242';
                                            const textDecoration = prod.deleted ? 'line-through' : 'none';

                                            return (
                                                <DeletableChip
                                                    key={`PROD_${prod.id}`}
                                                    disabled={
                                                        editMode &&
                                                        !prod.added &&
                                                        selectedOptions?.scopeType === PRODUCT_SCOPE_TYPE.VALID_FOR
                                                    }
                                                    onDelete={() => this.deleteById(prod.id)}
                                                    showAlert={showAlert}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.75,
                                                            py: 0.75,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: textColor,
                                                                textDecoration,
                                                                fontWeight: 'bold',
                                                                fontSize: 14,
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            Produkt: {prod.name} ({prod.id})
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                color: textColor,
                                                                textDecoration,
                                                                fontSize: 14,
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            Shop: {prod.shopInfo?.name}, {prod.shopInfo?.street}{' '}
                                                            {prod.shopInfo?.houseNumber}, {prod.shopInfo?.zip}{' '}
                                                            {prod.shopInfo?.city}
                                                        </Typography>

                                                        {selectedOptions?.scopeType === PRODUCT_SCOPE_TYPE.VALID_FOR &&
                                                            (editMode && !prod.added ? (
                                                                prod.minQty != null && (
                                                                    <Typography
                                                                        sx={{
                                                                            color: textColor,
                                                                            textDecoration,
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        Mindestmenge: {prod.minQty}
                                                                    </Typography>
                                                                )
                                                            ) : (
                                                                (!editMode || this.state.mustHaveMinQty) && (
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            flexDirection: 'row',
                                                                            justifyContent: 'center',
                                                                        }}
                                                                    >
                                                                        <DeletableNumericInput
                                                                            maxLength={2}
                                                                            value={prod.minQty}
                                                                            setValue={(val) =>
                                                                                setSelectedProducts?.(
                                                                                    selectedProducts.map((p) => {
                                                                                        if (
                                                                                            this.normalize(p.id) ===
                                                                                            this.normalize(prod.id)
                                                                                        ) {
                                                                                            return { ...p, minQty: val };
                                                                                        }
                                                                                        return p;
                                                                                    })
                                                                                )
                                                                            }
                                                                            placeholder="Mindestmenge"
                                                                            showPlaceholderTitle
                                                                        />
                                                                    </Box>
                                                                )
                                                            ))}

                                                        {prod.deleted && (
                                                            <Box
                                                                onClick={() => this.reselectById(prod.id)}
                                                                sx={{
                                                                    backgroundColor: '#dffbdf',
                                                                    mt: 0.75,
                                                                    borderRadius: '6px',
                                                                    p: 1,
                                                                    cursor: 'pointer',
                                                                    alignSelf: 'flex-start',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        color: '#4d714d',
                                                                        textAlign: 'center',
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    Wiederherstellen
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </DeletableChip>
                                            );
                                        })}
                                    </Box>
                                )}
                            </PaginationWrapper>
                        )}

                        <CustomLightButton
                            label="Produkt hinzufügen"
                            icon={require('../../../../../assets/icons/dairyproducts.png')}
                            onPress={() => onSelectionPressed?.()}
                        />
                    </>
                )}
            </Box>
        );
    }
}

export default ProductConfigSection;